import { Fragment, useState } from "react";
import useSWR from "swr";

import { api, ApiError, IndexSeriesResponse, IndexSummary, UserMe } from "../api";
import { PageHeader } from "../ui";

const fetcher = <T,>(path: string) => api.get<T>(path);

// Index dates are date-only (YYYY-MM-DD); rendering them raw avoids the
// timezone shift `new Date()` introduces on date-only strings.
export function num(v: string | null, digits = 2) {
  return v === null ? "—" : Number(v).toFixed(digits);
}

/** RoR is stored as a fraction (0.01 = 1%). */
export function pct(v: string | number | null, digits = 2) {
  return v === null ? "—" : `${(Number(v) * 100).toFixed(digits)}%`;
}

/** Compounded return since the series start, as a fraction.
 *
 * Nulls count as no move. Returns are chained, not summed, so the curve is a
 * true growth path rather than an approximation of one.
 */
export function cumulative(rors: (string | null)[]) {
  let growth = 1;
  return rors.map((r) => {
    growth *= 1 + (r === null ? 0 : Number(r));
    return growth - 1;
  });
}

export function rorClass(v: string | number | null) {
  if (v === null) return "text-muted";
  const n = Number(v);
  if (n > 0) return "text-pos";
  if (n < 0) return "text-neg";
  return "text-muted";
}

function Sparkline({ values, label }: { values: number[]; label: string }) {
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * 100;
      const y = span === 0 ? 15 : 30 - ((v - min) / span) * 30;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
  // Baseline only when the curve actually crosses zero.
  const zeroY = min < 0 && max > 0 ? 30 - ((0 - min) / span) * 30 : null;

  return (
    <svg
      viewBox="0 0 100 30"
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      className="w-full h-16 text-accent"
    >
      {zeroY !== null && (
        <line
          x1="0"
          y1={zeroY}
          x2="100"
          y2={zeroY}
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="2 2"
          opacity="0.3"
          vectorEffect="non-scaling-stroke"
        />
      )}
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export function Indices({ user }: { user: UserMe }) {
  const { data: indices, error } = useSWR<IndexSummary[]>("/metadata/indices", fetcher, {
    shouldRetryOnError: false,
  });
  const [selected, setSelected] = useState<IndexSummary | null>(null);
  const [freq, setFreq] = useState("monthly");

  const freeTier = user.tier.name === "free";

  const toggle = (ix: IndexSummary) => {
    if (selected?.type === ix.type) {
      setSelected(null);
      return;
    }
    setSelected(ix);
    setFreq(ix.frequencies.includes("monthly") ? "monthly" : ix.frequencies[0]);
  };

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        title="Indices"
        description={
          <>
            NilssonHedge aggregate strategy indices, available on every plan including Free.
            {freeTier && user.tier.history_years !== null && (
              <> Free is limited to monthly data and the last {user.tier.history_years} years.</>
            )}
          </>
        }
      />

      {error && (
        <p className="text-sm text-neg">
          Could not load indices{error instanceof ApiError ? ` (${error.status})` : ""}.
        </p>
      )}
      {!indices && !error && <p className="text-sm text-muted">Loading...</p>}
      {indices && indices.length === 0 && (
        <p className="text-sm text-muted">No indices available.</p>
      )}

      {indices && indices.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Index</th>
                <th>Frequencies</th>
                <th>Range</th>
              </tr>
            </thead>
            <tbody>
              {indices.map((ix) => {
                const open = selected?.type === ix.type;
                return (
                  <Fragment key={ix.type}>
                    <tr
                      tabIndex={0}
                      aria-expanded={open}
                      onClick={() => toggle(ix)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggle(ix);
                        }
                      }}
                      className={`cursor-pointer hover:bg-stat focus:bg-stat outline-none ${open ? "bg-stat" : ""}`}
                    >
                      <td className="text-main font-medium">
                        <span aria-hidden className="inline-block w-3 text-muted">
                          {open ? "▾" : "▸"}
                        </span>
                        {ix.type}
                      </td>
                      <td>{ix.frequencies.join(", ")}</td>
                      <td className="font-mono whitespace-nowrap">
                        {ix.first_date} → {ix.last_date}
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={3} className="p-0 bg-stat">
                          <IndexSeries
                            index={ix}
                            freq={freq}
                            onFreq={setFreq}
                            freeTier={freeTier}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function IndexSeries({
  index,
  freq,
  onFreq,
  freeTier,
}: {
  index: IndexSummary;
  freq: string;
  onFreq: (f: string) => void;
  freeTier: boolean;
}) {
  const { data, error, isLoading } = useSWR<IndexSeriesResponse>(
    `/market-data/timeseries?index_type=${encodeURIComponent(index.type)}&frequency=${freq}`,
    fetcher,
    { shouldRetryOnError: false },
  );

  // API returns oldest-first; show newest-first so the latest point is on top.
  const rows = data ? [...data.items].reverse() : [];
  // Chart keeps the API's chronological order.
  const curve = data ? cumulative(data.items.map((p) => p.ror)) : [];
  const net = curve.length > 0 ? curve[curve.length - 1] : 0;

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {index.frequencies.map((f) => {
          const blocked = freeTier && f !== "monthly";
          return (
            <button
              key={f}
              className={`btn-sm ${freq === f ? "btn-primary" : "btn-default"}`}
              disabled={blocked}
              onClick={() => onFreq(f)}
            >
              {f}
            </button>
          );
        })}
        {freeTier && index.frequencies.some((f) => f !== "monthly") && (
          <span className="text-[11px] text-muted">Daily isn't on the Free plan</span>
        )}
      </div>

      {isLoading && <p className="text-xs text-muted">Loading series...</p>}
      {error && (
        <p className="text-xs text-neg">
          Could not load series{error instanceof ApiError ? ` (${error.status})` : ""}.
        </p>
      )}

      {data && data.truncated && rows.length > 0 && (
        <p className="text-[11px] text-muted">
          Showing {rows.length} points ({rows[rows.length - 1].date} → {rows[0].date}) of a longer
          series — your plan caps results per request.
        </p>
      )}

      {data && rows.length === 0 && !isLoading && (
        <p className="text-xs text-muted">No data for this frequency.</p>
      )}

      {curve.length > 1 && (
        <div className="flex flex-col gap-1">
          <Sparkline
            values={curve}
            label={`Cumulative RoR for ${index.type}, ${data!.items[0].date} to ${
              data!.items[data!.items.length - 1].date
            }, net ${pct(net)}`}
          />
          <div className="flex items-baseline justify-between text-[11px] text-muted font-mono">
            <span>{data!.items[0].date}</span>
            <span className={rorClass(net)}>
              cumulative RoR {net > 0 ? "+" : ""}
              {pct(net)}
            </span>
            <span>{data!.items[data!.items.length - 1].date}</span>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="table-wrap max-h-96 overflow-y-auto">
          <table className="data-table">
            <thead className="sticky top-0">
              <tr>
                <th>Date</th>
                <th className="text-right">RoR</th>
                <th className="text-right">Funds</th>
                <th className="text-right">AUM (bn)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((pt) => (
                <tr key={pt.date}>
                  <td className="font-mono text-main">{pt.date}</td>
                  <td className={`text-right font-mono ${rorClass(pt.ror)}`}>
                    {pct(pt.ror)}
                  </td>
                  <td className="text-right">
                    {pt.cnt === null ? "—" : pt.cnt.toLocaleString()}
                  </td>
                  <td className="text-right font-mono">{num(pt.aum_bn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
